/**
 * Normalize theme list fields from fetchThemeData: API may return arrays or JSON-encoded strings.
 * (Admin themeupdate uses the same logic as ensureArray.)
 */
export function ensureThemeJsonArray(val) {
  if (Array.isArray(val)) return val;
  if (val == null || val === "") return [];
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Build a safe URL for theme images (S3 keys under REACT_APP_S3_PATH, or full https URLs).
 * Encodes each path segment so spaces, parentheses, %, etc. resolve to the correct object key.
 */
export function getDvSourceImageUrl(imgPath) {
  if (!imgPath) return "";
  const s = String(imgPath).trim();
  if (!s) return "";
  const encodePathSegments = (relativePath) =>
    relativePath
      .split("/")
      .map((seg) => {
        if (!seg) return seg;
        try {
          return encodeURIComponent(decodeURIComponent(seg));
        } catch {
          return encodeURIComponent(seg);
        }
      })
      .join("/");
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const parts = u.pathname.split("/").filter(Boolean);
      u.pathname = `/${parts.map((seg) => encodePathSegments(seg)).join("/")}`;
      return u.toString();
    } catch {
      return encodeURI(s);
    }
  }
  const base = (process.env.REACT_APP_S3_PATH || "").replace(/\/?$/, "/");
  const path = s.replace(/^\/+/, "");
  return `${base}${encodePathSegments(path)}`;
}

/**
 * Value for CSS `background-image` / `url(...)`. Unquoted `url(https://.../a(2).png)` is invalid — the `(` ends the URL.
 */
export function toCssUrlValue(href) {
  if (!href) return "none";
  const escaped = String(href).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `url("${escaped}")`;
}

/**
 * Theme / question images. Short names (< 25 chars) are the bundled defaults in /public/uploads,
 * longer names are files uploaded to S3 (same rule as the original game's possibleOnS3()).
 */
export function assetUrl(name) {
  if (!name) return "";
  const s = String(name).trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (s.length < 25) return `${process.env.PUBLIC_URL || ""}/uploads/${encodeURIComponent(s)}`;
  return getDvSourceImageUrl(s);
}

/** Colours the admin can set on a theme, with the original game's defaults. */
export function themeColors(theme) {
  const t = theme || {};
  return {
    box: t.questionbox_color || "#0000FF", // buttons, timer, points, number badge
    text: t.landing_page_title_color || "#000000", // titles, rules, question text
    button: t.button_text_color || "#ffffff", // text on the buttons
    answer: t.answer_color || "#0000FF", // the option the player picked
    yes: t.main_color1 || "#FF0000",
    maybe: t.main_color2 || "#0000FF",
    no: t.main_color3 || "#008000",
  };
}

/** CSS variables every player page reads its colours from. */
export function themeCssVars(theme) {
  const c = themeColors(theme);
  return {
    "--iq-box": c.box,
    "--iq-title": c.text,
    "--iq-btn-text": c.button,
    "--iq-answer": c.answer,
    "--iq-yes": c.yes,
    "--iq-maybe": c.maybe,
    "--iq-no": c.no,
  };
}

/** Theme flags are stored as the strings "true"/"false". */
export function isTrue(v) {
  return v === true || v === 1 || String(v).toLowerCase() === "true" || String(v) === "1";
}

/** The logged-in player saved by loginSlice. */
export function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "null");
  } catch {
    return null;
  }
}

/** Bearer token for the game API (`sessionId&organizationId`). */
export function getUserToken(user) {
  return user ? `${user.sessionId}&${user.organizationId}` : "";
}
